import APIService, { API_URL } from './apiservice';
import { AuthHeader } from './authheader';

// SSE stream endpoints (relative to `route`). The bubble defaults to Eli, the
// router agent; the recruitment and HR copilot endpoints are kept so callers
// can still target them explicitly, and nothing about either path is removed.
export const CHAT_HR_STREAM_ENDPOINT = 'chat-hr/stream';
export const RECRUITMENT_STREAM_ENDPOINT = 'recruitment/stream';
export const ELI_STREAM_ENDPOINT = 'eli/stream';

const createAIAgentService = (route='api/v1/copilot') => {
	// FIX C3: the backend reads the outgoing conversation id as snake_case
	// `conversation_id`. The redux state (and this service's callers) uses the
	// camelCase `conversationId` field internally, so translate right before
	// the payload is handed off for serialization. Other payload fields are
	// kept as-is.
	const withConversationId = (payload) => ({
		...payload,
		conversation_id: payload.conversationId ?? payload.conversation_id ?? null,
	});

	const invokeAgent = async (data, endpoint) => {
		const path = `${route}/${endpoint}`;
    	return APIService.doRequest(path, 'POST', withConversationId(data));
  	};

  	// Query string built only from the keys actually supplied, so calling
  	// getChatHistory() with no arguments produces exactly the URL it always
  	// has. The existing bubble load and the existing backend tests both rely
  	// on the bare path staying identical.
  	const buildQuery = (pairs) => {
	    const query = new URLSearchParams();
	    Object.entries(pairs).forEach(([key, value]) => {
	      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
	    });
	    const qs = query.toString();
	    return qs ? `?${qs}` : '';
  	};

  	const getChatHistory = ({ conversationId, agentKey } = {}) =>
	    APIService.doRequest(
	      `${route}/chat-hr/history${buildQuery({ conversation_id: conversationId, agent_key: agentKey })}`,
	      'GET'
	    );

  	const getConversations = ({ agentKey, offset } = {}) =>
	    APIService.doRequest(
	      `${route}/chat-hr/conversations${buildQuery({ agent_key: agentKey, offset })}`,
	      'GET'
	    );

  	// Streams the chat-hr reply as SSE `data: {...}` frames (delta/done/fallback).
  	// Bypasses APIService.doRequest (which cannot stream) so it calls fetch
  	// directly; this also means it does not go through doRequest's
  	// isTokenExpired()/401-logout handling, so a mid-stream 401 falls back
  	// via onFallback rather than the app's normal logout. Acceptable for v1.
  	// `signal` (optional) is an AbortSignal supplied by the caller (the saga
  	// creates the AbortController so it can abort on cancellation - see
  	// createChatStreamChannel in redux/copilot/saga.jsx).
  	// `endpoint` selects the stream target and defaults to Eli, the router
  	// agent; pass RECRUITMENT_STREAM_ENDPOINT or CHAT_HR_STREAM_ENDPOINT to
  	// target the recruitment or HR copilot directly instead, which both stay
  	// reachable while Eli is proven out. The SSE parsing below is identical
  	// for all three (same frame contract).
  	const streamChat = async (payload, { onDelta, onDone, onFallback, onError, onArtifact }, signal, endpoint = ELI_STREAM_ENDPOINT) => {
	    try {
	      const res = await fetch(`${API_URL}/${route}/${endpoint}`, {
	        method: 'POST',
	        headers: AuthHeader({ 'Content-Type': 'application/json' }),
	        body: JSON.stringify(withConversationId(payload)),
	        ...(signal ? { signal } : {}),
	      });
	      if (!res.ok || !res.body) { onFallback(); return; }
	      const reader = res.body.getReader();
	      const decoder = new TextDecoder();
	      let buf = '';
	      // FIX I4: if the connection drops (or the server sends nothing) before
	      // a single delta/done/fallback frame is seen, treat it as a dropped
	      // stream and fall back instead of silently calling onDone() with no
	      // content (which left a blank bubble on screen).
	      let sawFrame = false;
	      while (true) {
	        const { value, done } = await reader.read();
	        if (done) break;
	        buf += decoder.decode(value, { stream: true });
	        const frames = buf.split('\n\n');
	        buf = frames.pop();
	        for (const f of frames) {
	          const line = f.replace(/^data: /, '').trim();
	          if (!line) continue;
	          const evt = JSON.parse(line);
	          sawFrame = true;
	          // FIX minor 7: `if (evt.delta)` treats an empty-string delta as
	          // falsy and drops it; check the type instead so "" tokens flow.
	          if (typeof evt.delta === 'string') onDelta(evt.delta);
	          // A download turn sends an artifact frame, then a separate done frame.
	          // Deliberately NO `return` here: the neighbouring branches return, and
	          // copying that would stop onDone from firing and leave the bubble stuck
	          // showing "Eli is thinking...". onArtifact is optional so existing callers
	          // are unaffected.
	          else if (evt.artifact) { if (onArtifact) onArtifact(evt.artifact); }
	          else if (evt.fallback) { onFallback(); return; } // covers {error:true, fallback:true} too
	          else if (evt.done) { onDone(); return; }
	        }
	      }
	      if (sawFrame) onDone();
	      else onFallback();
	    } catch (e) {
	      onError(e);
	    }
  	};

  	return {
	    invokeAgent,
	    getChatHistory,
	    getConversations,
	    streamChat,
  	};
};

export default createAIAgentService;
